import React, { useState, useEffect } from 'react';
import { GraduationCap } from 'lucide-react';
import { SEO } from '../components/SEO';

interface GradeState {
  fifthGrade: string;
  sixthGrade: string;
  seventh: {
    hrvatski: string;
    matematika: string;
    engleski: string;
    prosjek: string;
  };
  eighth: {
    hrvatski: string;
    matematika: string;
    engleski: string;
    prosjek: string;
  };
}

function KalkulatorBodova() {
  const [values, setValues] = useState<GradeState>({
    fifthGrade: '',
    sixthGrade: '',
    seventh: {
      hrvatski: '',
      matematika: '',
      engleski: '',
      prosjek: '',
    },
    eighth: {
      hrvatski: '',
      matematika: '',
      engleski: '',
      prosjek: '',
    }
  });

  const [totalPoints, setTotalPoints] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<{
    fifthGrade: number;
    sixthGrade: number;
    seventh: number;
    eighth: number;
  } | null>(null);

  const calculatePoints = () => {
    // Convert grades to numbers
    const fifthGradePoints = (parseFloat(values.fifthGrade) || 0) * 5;
    const sixthGradePoints = (parseFloat(values.sixthGrade) || 0) * 5;

    // Calculate 7th grade points
    const seventhHrvatski = parseFloat(values.seventh.hrvatski) || 0;
    const seventhMatematika = parseFloat(values.seventh.matematika) || 0;
    const seventhEngleski = parseFloat(values.seventh.engleski) || 0;
    const seventhProsjek = parseFloat(values.seventh.prosjek) || 0;
    
    const seventhPoints = ((seventhHrvatski + seventhMatematika + seventhEngleski) / 3 + seventhProsjek) * 5;

    // Calculate 8th grade points
    const eighthHrvatski = parseFloat(values.eighth.hrvatski) || 0;
    const eighthMatematika = parseFloat(values.eighth.matematika) || 0;
    const eighthEngleski = parseFloat(values.eighth.engleski) || 0;
    const eighthProsjek = parseFloat(values.eighth.prosjek) || 0;
    
    const eighthPoints = ((eighthHrvatski + eighthMatematika + eighthEngleski) / 3 + eighthProsjek) * 5;

    // Calculate total points (max 100 points)
    const total = Math.min(100, fifthGradePoints + sixthGradePoints + seventhPoints + eighthPoints);

    setTotalPoints(parseFloat(total.toFixed(2)));
    setBreakdown({
      fifthGrade: parseFloat(fifthGradePoints.toFixed(2)),
      sixthGrade: parseFloat(sixthGradePoints.toFixed(2)),
      seventh: parseFloat(seventhPoints.toFixed(2)),
      eighth: parseFloat(eighthPoints.toFixed(2))
    });
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setValues(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof GradeState],
          [child]: value
        }
      }));
    } else {
      setValues(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  useEffect(() => {
    if (Object.values(values).some(val => val !== '')) {
      calculatePoints();
    }
  }, [values]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Bodova",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte bodove za upis u srednju školu. Precizni izračun na temelju ocjena iz osnovne škole.",
    "url": "https://online-kalkulator.com/kalkulator-bodova",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun bodova za srednju školu",
      "Izračun prosjeka ocjena",
      "Bodovanje predmeta",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Bodova | Izračun Bodova za Upis u Srednju Školu"
        description="Izračunajte bodove za upis u srednju školu. Precizni izračun na temelju ocjena iz osnovne škole. Jednostavan za korištenje i precizan."
        canonicalUrl="/kalkulator-bodova"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <GraduationCap className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Bodova za Srednju Školu</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* 5th and 6th Grade */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">5. i 6. razred</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prosjek 5. razred
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.01"
                      value={values.fifthGrade}
                      onChange={(e) => handleInputChange('fifthGrade', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prosjek 6. razred
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.01"
                      value={values.sixthGrade}
                      onChange={(e) => handleInputChange('sixthGrade', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                </div>
              </div>

              {/* 7th Grade */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">7. razred</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hrvatski jezik
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={values.seventh.hrvatski}
                      onChange={(e) => handleInputChange('seventh.hrvatski', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Matematika
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={values.seventh.matematika}
                      onChange={(e) => handleInputChange('seventh.matematika', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Engleski jezik
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={values.seventh.engleski}
                      onChange={(e) => handleInputChange('seventh.engleski', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ukupni prosjek
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.01"
                      value={values.seventh.prosjek}
                      onChange={(e) => handleInputChange('seventh.prosjek', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek svih ocjena"
                    />
                  </div>
                </div>
              </div>

              {/* 8th Grade */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">8. razred</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hrvatski jezik
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={values.eighth.hrvatski}
                      onChange={(e) => handleInputChange('eighth.hrvatski', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Matematika
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={values.eighth.matematika}
                      onChange={(e) => handleInputChange('eighth.matematika', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Engleski jezik
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={values.eighth.engleski}
                      onChange={(e) => handleInputChange('eighth.engleski', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek ocjena"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ukupni prosjek
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.01"
                      value={values.eighth.prosjek}
                      onChange={(e) => handleInputChange('eighth.prosjek', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Prosjek svih ocjena"
                    />
                  </div>
                </div>
              </div>

              {/* Results */}
              {totalPoints !== null && breakdown !== null && (
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Rezultati</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">5. razred:</span>
                      <span className="font-semibold">{breakdown.fifthGrade} / 25</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">6. razred:</span>
                      <span className="font-semibold">{breakdown.sixthGrade} / 25</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">7. razred:</span>
                      <span className="font-semibold">{breakdown.seventh} / 25</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">8. razred:</span>
                      <span className="font-semibold">{breakdown.eighth} / 25</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t">
                      <span className="text-gray-800 font-medium">Ukupno bodova:</span>
                      <span className="text-xl font-bold text-blue-600">{totalPoints} / 100</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Bodova</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator bodova za srednju školu pomaže vam izračunati ukupan broj bodova koji možete
                  ostvariti pri upisu u srednju školu na temelju vaših ocjena iz osnovne škole.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Način Bodovanja</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">5. i 6. razred (50 bodova)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Prosjek ocjena 5. razreda × 5 = max 25 bodova</li>
                      <li>Prosjek ocjena 6. razreda × 5 = max 25 bodova</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">7. i 8. razred (50 bodova)</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Prosjek ocjena iz Hrvatskog jezika</li>
                      <li>Prosjek ocjena iz Matematike</li>
                      <li>Prosjek ocjena iz Engleskog jezika</li>
                      <li>Ukupni prosjek svih predmeta</li>
                      <li>Formula: ((Hrvatski + Matematika + Engleski) / 3 + Prosjek) × 5</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite prosjek ocjena za 5. razred</li>
                  <li>Unesite prosjek ocjena za 6. razred</li>
                  <li>Za 7. i 8. razred unesite:</li>
                  <ul className="list-disc list-inside ml-8 mt-2 space-y-1">
                    <li>Prosjek ocjena iz Hrvatskog jezika</li>
                    <li>Prosjek ocjena iz Matematike</li>
                    <li>Prosjek ocjena iz Engleskog jezika</li>
                    <li>Ukupni prosjek svih predmeta</li>
                  </ul>
                  <li>Kalkulator će automatski izračunati vaš ukupni broj bodova</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Ocjene se unose s decimalnim mjestima (npr. 4.5)</li>
                  <li>Maksimalan broj bodova je 100</li>
                  <li>Bodovi se računaju automatski pri unosu ocjena</li>
                  <li>Izračun je informativan i može odstupati od stvarnog bodovanja</li>
                  <li>Za točne informacije o bodovanju, provjerite s željenom školom</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorBodova;